import React, { useState, useEffect } from 'react';
import { Monitor, LaptopMinimal, Trash2, Download, HelpCircle, Check, AlertTriangle, Shield, AlertCircle, ArrowRight, Globe, MousePointer2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AuthModal } from '../components/ui/authModal';
import { Navbar, Footer, Banner } from '../components/landing/Shared';

const DownloadPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();


  const [showSignInModal, setShowSignInModal] = useState(false);
  const [modalMode, setModalMode] = useState<'signin' | 'signup'>('signin');
  const [activeTab, setActiveTab] = useState<'windows' | 'mac' | 'web'>('windows');

  // Detect OS and set appropriate tab on mount
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('mac')) {
      setActiveTab('mac');
    } else if (userAgent.includes('win')) {
      setActiveTab('windows');
    }
  }, []);

  const handleDownload = (filename: string) => {
    // Google Analytics Event
    if ((window as any).gtag) {
      (window as any).gtag('event', 'download_click', {
        'event_category': 'Engagement',
        'event_label': 'Download Page',
        'file_name': filename,
        'platform': activeTab
      });
    }

    const link = document.createElement('a');
    // Using internal file paths as requested (placeholders or existing)
    link.href = `/downloads/${filename}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSignIn = () => {
    setModalMode('signin');
    setShowSignInModal(true);
  };

  const handleSignUp = () => {
    setModalMode('signup');
    setShowSignInModal(true);
  };

  const handleSignOut = () => {
    logout();
  };

  const handleDashboard = () => {
    navigate('/dashboard');
  };

  const handleHeroCTA = () => {
    // Already on download page, scroll to options
    const element = document.getElementById('download-options');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white text-[#1d1d1f] antialiased" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
      <Banner />
      <Navbar
        onSignIn={handleSignIn}
        onSignUp={handleSignUp}
        onRunClick={handleHeroCTA}
        isAuthenticated={isAuthenticated}
        onSignOut={handleSignOut}
        onDashboard={handleDashboard}
      />

      <AuthModal
        showModal={showSignInModal}
        onClose={() => setShowSignInModal(false)}
        initialMode={modalMode}
      />

      <main className="pt-32 pb-20 px-6 excel-grid min-h-screen">
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center mb-8">
            <h1 className="text-[48px] md:text-[56px] font-semibold text-[#1d1d1f] mb-6 tracking-[-0.02em] leading-[1.1]">
              Download Alt-X for Excel
            </h1>
            <div className="bg-gradient-to-r from-blue-50/40 via-blue-100/30 to-blue-50/40 border border-blue-100/30 rounded-2xl p-6 max-w-2xl mx-auto">
              <p className="text-[19px] text-[#6e6e73] leading-[1.5]" style={{ color: 'rgba(29, 29, 31, 0.99)', fontWeight: 600 }}>
                As part of the public beta, the Alt-X add-in is distributed directly by installer to ensure you get the latest features and updates instantly, before we release the on the Office Add-in Store.
              </p>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-[32px] p-2 shadow-2xl mb-12 liquid-glass shadow-black/10">
            <div className="flex p-1 bg-black/5 rounded-[24px] mb-8">
              <button
                onClick={() => setActiveTab('windows')}
                className={`flex-1 py-3 px-6 rounded-[20px] text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${activeTab === 'windows'
                  ? 'bg-white text-[#1d1d1f] shadow-sm'
                  : 'text-[#6e6e73] hover:text-[#1d1d1f]'
                  }`}
              >
                <Monitor size={18} />
                Windows
              </button>
              <button
                onClick={() => setActiveTab('mac')}
                className={`flex-1 py-3 px-6 rounded-[20px] text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${activeTab === 'mac'
                  ? 'bg-white text-[#1d1d1f] shadow-sm'
                  : 'text-[#6e6e73] hover:text-[#1d1d1f]'
                  }`}
              >
                <LaptopMinimal size={18} />
                macOS
              </button>
              <button
                onClick={() => setActiveTab('web')}
                className={`flex-1 py-3 px-6 rounded-[20px] text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${activeTab === 'web'
                  ? 'bg-white text-[#1d1d1f] shadow-sm'
                  : 'text-[#6e6e73] hover:text-[#1d1d1f]'
                  }`}
              >
                <Globe size={18} />
                Excel for Web
              </button>
            </div>

            <div className="px-6 pb-8 md:px-10 md:pb-10">
              {activeTab === 'windows' && (
                <div className="space-y-8 animate-fade-in">
                  <div className="flex flex-col gap-8 items-stretch">
                    <div className="bg-blue-50/50 border border-blue-100 rounded-2xl p-6">
                      <h3 className="font-semibold text-[#1d1d1f] mb-4 flex items-center gap-2">
                        <Download size={18} className="text-blue-600" />
                        Step 1: Download
                      </h3>
                      <button
                        onClick={() => handleDownload('AltX-Windows-Setup.exe')}
                        className="w-full bg-black hover:bg-zinc-800 text-white font-medium py-3 px-6 rounded-full flex items-center justify-center gap-2 transition-all shadow-lg shadow-black/20 active:scale-[0.98]"
                      >
                        Download for Windows (64-bit)
                      </button>
                      <p className="text-xs text-[#86868b] mt-3 text-center">v1.2.0 • Windows 10/11</p>
                    </div>

                    <div className="bg-white/40 border border-zinc-200/50 rounded-2xl p-6">
                      <h3 className="font-semibold text-[#1d1d1f] mb-4 flex items-center gap-2">
                        <Shield size={18} className="text-green-600" />
                        Step 2: Install & Security
                      </h3>
                      <div className="pl-6 border-l-2 border-zinc-100 space-y-4">
                        <p className="text-sm text-[#6e6e73]">Double-click the downloaded <code className="bg-zinc-100 px-1.5 py-0.5 rounded text-xs">AltX-Windows-Setup.exe</code>.</p>

                        <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4">
                          <p className="text-sm text-[#6e6e73] mb-2">
                            If you see "Windows protected your PC":
                          </p>
                          <ol className="list-decimal list-inside text-sm text-[#6e6e73] space-y-1 ml-1">
                            <li>Click "More info"</li>
                            <li>Click "Run anyway"</li>
                          </ol>
                        </div>

                        <p className="text-sm text-[#6e6e73]">
                          <strong>Troubleshooting:</strong> If the file is blocked, right-click the .exe file → Properties → Check "Unblock" at the bottom → Apply.
                        </p>
                      </div>
                    </div>

                    <div className="bg-white/40 border border-zinc-200/50 rounded-2xl p-6">
                      <h3 className="font-semibold text-[#1d1d1f] mb-4 flex items-center gap-2">
                        <Check size={18} className="text-green-600" />
                        Step 3: Setup in Excel
                      </h3>
                      <ul className="space-y-3 text-sm text-[#6e6e73]">
                        <li className="flex gap-3">
                          <span className="w-6 h-6 rounded-full bg-zinc-100 flex items-center justify-center text-xs font-bold text-zinc-500 shrink-0">1</span>
                          <span className="py-0.5">Open Microsoft Excel.</span>
                        </li>
                        <li className="flex gap-3">
                          <span className="w-6 h-6 rounded-full bg-zinc-100 flex items-center justify-center text-xs font-bold text-zinc-500 shrink-0">2</span>
                          <span className="py-0.5">You should see a new "Alt-X" tab in the ribbon.</span>
                        </li>
                        <li className="flex gap-3">
                          <span className="w-6 h-6 rounded-full bg-zinc-100 flex items-center justify-center text-xs font-bold text-zinc-500 shrink-0">3</span>
                          <span className="py-0.5">Click "Log In" to activate your account.</span>
                        </li>
                      </ul>

                      <div className="pt-6 border-t border-zinc-100 mt-6">
                        <button
                          onClick={() => handleDownload('AltX-Windows-Uninstaller.exe')}
                          className="text-xs text-[#86868b] hover:text-[#1d1d1f] flex items-center gap-2 transition-colors"
                        >
                          <Trash2 size={12} />
                          Need to uninstall? Download Uninstaller
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'mac' && (
                <div className="space-y-8 animate-fade-in">
                  <div className="flex flex-col gap-8 items-stretch">
                    <div className="bg-blue-50/50 border border-blue-100 rounded-2xl p-6">
                      <h3 className="font-semibold text-[#1d1d1f] mb-4 flex items-center gap-2">
                        <Download size={18} className="text-blue-600" />
                        Step 1: Download
                      </h3>
                      <button
                        onClick={() => handleDownload('AltX-Mac-Installer.pkg')}
                        className="w-full bg-black hover:bg-zinc-800 text-white font-medium py-3 px-6 rounded-full flex items-center justify-center gap-2 transition-all shadow-lg shadow-black/20 active:scale-[0.98]"
                      >
                        Download for macOS
                      </button>
                      <p className="text-xs text-[#86868b] mt-3 text-center">v1.2.0 • macOS 12+</p>
                    </div>

                    <div className="bg-white/40 border border-zinc-200/50 rounded-2xl p-6">
                      <h3 className="font-semibold text-[#1d1d1f] mb-4 flex items-center gap-2">
                        <Check size={18} className="text-green-600" />
                        Step 2: Setup in Excel
                      </h3>
                      <ul className="space-y-3 text-sm text-[#6e6e73]">
                        <li className="flex gap-3">
                          <span className="w-6 h-6 rounded-full bg-zinc-100 flex items-center justify-center text-xs font-bold text-zinc-500 shrink-0">1</span>
                          <span className="py-0.5">Open Microsoft Excel.</span>
                        </li>
                        <li className="flex gap-3">
                          <span className="w-6 h-6 rounded-full bg-zinc-100 flex items-center justify-center text-xs font-bold text-zinc-500 shrink-0">2</span>
                          <span className="py-0.5">Look for the "Alt-X" tab in the ribbon.</span>
                        </li>
                        <li className="flex gap-3">
                          <span className="w-6 h-6 rounded-full bg-zinc-100 flex items-center justify-center text-xs font-bold text-zinc-500 shrink-0">3</span>
                          <span className="py-0.5">Sign in to start using the add-in.</span>
                        </li>
                      </ul>

                      <div className="pt-6 border-t border-zinc-100 mt-6">
                        <button
                          onClick={() => handleDownload('AltX-Mac-Uninstaller.pkg')}
                          className="text-xs text-[#86868b] hover:text-[#1d1d1f] flex items-center gap-2 transition-colors"
                        >
                          <Trash2 size={12} />
                          Need to uninstall? Download Uninstaller
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'web' && (
                <div className="space-y-8 animate-fade-in">
                  <div className="flex flex-col gap-8 items-stretch">
                    {/* Video Tutorial Section */}
                    <div className="bg-gradient-to-br from-blue-50/40 via-blue-100/30 to-purple-50/40 border border-blue-100/30 rounded-2xl p-6">
                      <h3 className="font-semibold text-[#1d1d1f] mb-4 flex items-center gap-2">
                        <MousePointer2 size={18} className="text-purple-600" />
                        Watch: How to Install in Excel Web
                      </h3>
                      <div className="relative aspect-video bg-black rounded-xl overflow-hidden mb-4">
                        <video 
                          className="w-full h-full object-cover"
                          controls
                          poster="/images/video-thumbnail-excel-web.jpg"
                        >
                          <source src="/videos/excel-web-installation.mp4" type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      </div>
                      <p className="text-sm text-[#6e6e73] text-center">
                        Watch this 15-second guide to see how to upload the add-in
                      </p>
                    </div>

                    <div className="bg-blue-50/50 border border-blue-100 rounded-2xl p-6">
                      <h3 className="font-semibold text-[#1d1d1f] mb-4 flex items-center gap-2">
                        <Download size={18} className="text-blue-600" />
                        Step 1: Download Manifest
                      </h3>
                      <button
                        onClick={() => handleDownload('manifest-altx.xml')}
                        className="w-full bg-black hover:bg-zinc-800 text-white font-medium py-3 px-6 rounded-full flex items-center justify-center gap-2 transition-all shadow-lg shadow-black/20 active:scale-[0.98]"
                      >
                        Download Manifest File
                      </button>
                      <p className="text-xs text-[#86868b] mt-3 text-center">Required for Excel Online Sideloading</p>
                    </div>

                    <div className="bg-white/40 border border-zinc-200/50 rounded-2xl p-6">
                      <h3 className="font-semibold text-[#1d1d1f] mb-4 flex items-center gap-2">
                        <Globe size={18} className="text-green-600" />
                        Step 2: Upload to Excel Online
                      </h3>
                      <div className="pl-6 border-l-2 border-zinc-100 space-y-4">
                        <p className="text-sm text-[#6e6e73]">
                          Go to <a href="https://office.com/launch/excel" target="_blank" rel="noopener noreferrer" className="text-[#0071e3] hover:underline">Excel Online</a> and open a workbook.
                        </p>

                        <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-4 space-y-3">
                          <ol className="list-decimal list-inside text-sm text-[#6e6e73] space-y-2">
                            <li>Go to the <span className="font-semibold text-[#1d1d1f]">Insert</span> tab.</li>
                            <li>Click <span className="font-semibold text-[#1d1d1f]">Add-ins</span>.</li>
                            <li>Select <span className="font-semibold text-[#1d1d1f]">Manage My Add-ins</span> → <span className="font-semibold text-[#1d1d1f]">Upload My Add-in</span>.</li>
                            <li>Browse and select the <code className="bg-white border border-zinc-200 px-1.5 py-0.5 rounded text-xs">manifest.xml</code> file you downloaded.</li>
                          </ol>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white/40 border border-zinc-200/50 rounded-2xl p-6">
                      <h3 className="font-semibold text-[#1d1d1f] mb-4 flex items-center gap-2">
                        <Check size={18} className="text-green-600" />
                        Step 3: Setup Complete
                      </h3>
                      <ul className="space-y-3 text-sm text-[#6e6e73]">
                        <li className="flex gap-3">
                          <span className="w-6 h-6 rounded-full bg-zinc-100 flex items-center justify-center text-xs font-bold text-zinc-500 shrink-0">1</span>
                          <span className="py-0.5">The Alt-X tab should appear in the ribbon.</span>
                        </li>
                        <li className="flex gap-3">
                          <span className="w-6 h-6 rounded-full bg-zinc-100 flex items-center justify-center text-xs font-bold text-zinc-500 shrink-0">2</span>
                          <span className="py-0.5">Click any button in the Alt-X tab to open the task pane.</span>
                        </li>
                        <li className="flex gap-3">
                          <span className="w-6 h-6 rounded-full bg-zinc-100 flex items-center justify-center text-xs font-bold text-zinc-500 shrink-0">3</span>
                          <span className="py-0.5">Log in to start using Alt-X on the web.</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/60 backdrop-blur-sm border border-zinc-200 rounded-[24px] p-8">
              <h3 className="font-semibold text-[#1d1d1f] mb-4 flex items-center gap-2">
                <Check size={18} className="text-green-600" />
                What you'll need
              </h3>
              <ul className="space-y-3">
                <li className="text-sm text-[#6e6e73] flex items-start gap-2">
                  <span className="block w-1.5 h-1.5 rounded-full bg-zinc-300 mt-1.5"></span>
                  Latest version of Microsoft Excel (or Office 365)
                </li>
                <li className="text-sm text-[#6e6e73] flex items-start gap-2">
                  <span className="block w-1.5 h-1.5 rounded-full bg-zinc-300 mt-1.5"></span>
                  Administrator permissions might be required for installation
                </li>
                <li className="text-sm text-[#6e6e73] flex items-start gap-2">
                  <span className="block w-1.5 h-1.5 rounded-full bg-zinc-300 mt-1.5"></span>
                  Active internet connection for activation
                </li>
              </ul>
            </div>

            <div className="bg-white/60 backdrop-blur-sm border border-zinc-200 rounded-[24px] p-8">
              <h3 className="font-semibold text-[#1d1d1f] mb-4 flex items-center gap-2">
                <HelpCircle size={18} className="text-blue-600" />
                Need help?
              </h3>
              <p className="text-sm text-[#6e6e73] mb-4">
                If you're having trouble installing, please take a screenshot of any error message you see.
              </p>
              <a
                href="mailto:team@alt-x.co"
                className="text-sm font-semibold text-[#0071e3] hover:underline inline-flex items-center gap-1"
              >
                Contact Support <ArrowRight size={14} />
              </a>
              <div className="mt-4 p-3 bg-zinc-50 rounded-xl border border-zinc-100">
                <p className="text-[11px] text-[#86868b] flex items-start gap-1.5">
                  <AlertCircle size={12} className="mt-0.5" />
                  Security Note: Only download Alt-X installers directly from this page to ensure authenticity.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default DownloadPage;
